import { forwardRef, useRef, useCallback } from 'react';
import classNames from 'classnames';

const MULTILINE_HEIGHT_THRESHOLD = 18; // unscaled px — overlay fields at or above this are treated as multiline
const PDF_MULTILINE_LEADING = 1.15;
// A glyph box (cap height + descender + a little leading) is roughly 1.25x the
// font size for the fonts used here. Single-line inputs cap their font size at
// contentHeight / this ratio so descenders (g, q, y) are never clipped.
const GLYPH_BOX_RATIO = 1.25;
const MIN_FONT_SIZE = 6;

// Shared off-screen span for measuring text width accurately
let measureSpan = null;
function getMeasureSpan() {
  if (!measureSpan) {
    measureSpan = document.createElement('span');
    measureSpan.style.cssText =
      'position:absolute;visibility:hidden;white-space:pre;pointer-events:none;top:-9999px;left:-9999px;';
    document.body.appendChild(measureSpan);
  }
  return measureSpan;
}

function measureTextWidth(text, el) {
  const span = getMeasureSpan();
  const cs = window.getComputedStyle(el);
  span.style.font = cs.font;
  span.style.fontSize = cs.fontSize;
  span.style.fontFamily = cs.fontFamily;
  span.style.fontWeight = cs.fontWeight;
  span.style.fontStyle = cs.fontStyle;
  span.style.letterSpacing = cs.letterSpacing;
  span.textContent = text;
  return span.offsetWidth;
}

const TextField = forwardRef(({ field, value, onChange, style, required, maxLength, multiline, limitToBox = true }, ref) => {
  const textareaRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-detect multiline more aggressively because detected PDF text areas are often stored with compact heights.
  const isMultiline = multiline !== undefined ? multiline : (field.height >= MULTILINE_HEIGHT_THRESHOLD);

  // Binary-search helper for single-line: find the longest prefix that fits
  const fitToWidth = useCallback((text, el, availableWidth) => {
    let lo = 0, hi = text.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (measureTextWidth(text.substring(0, mid), el) <= availableWidth) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    return text.substring(0, lo);
  }, []);

  // Binary-search helper for multiline: find the longest prefix that fits in the textarea
  // Uses the real textarea element for accurate measurement
  const fitTextToTextarea = useCallback((text, el) => {
    const prevValue = el.value;
    const maxH = el.clientHeight;
    let lo = 0, hi = text.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      el.value = text.substring(0, mid);
      if (el.scrollHeight <= maxH + 1) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    // Verify the result actually fits
    el.value = text.substring(0, lo);
    while (lo > 0 && el.scrollHeight > maxH + 1) {
      lo--;
      el.value = text.substring(0, lo);
    }
    el.value = prevValue;
    return text.substring(0, lo);
  }, []);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    const currentVal = value || '';

    if (isMultiline && textareaRef.current) {
      const el = textareaRef.current;

      // Explicitly set the value and force layout to get accurate scrollHeight
      el.value = newValue;
      if (el.scrollHeight > el.clientHeight + 1) {
        // Overflow — truncate from the end to fit
        const truncated = fitTextToTextarea(newValue, el);
        el.value = truncated;
        if (truncated.length > 0) {
          onChange(field.name, truncated);
        } else {
          el.value = currentVal;
          onChange(field.name, currentVal);
        }
        return;
      }
    } else if (!isMultiline && limitToBox && inputRef.current) {
      const el = inputRef.current;
      // padding: 2px 4px => 8px horizontal padding + 2px safety margin
      const availableWidth = el.clientWidth - 10;
      if (availableWidth > 0) {
        const textWidth = measureTextWidth(newValue, el);
        if (textWidth > availableWidth) {
          // Field is full — reject the input, keep current value
          onChange(field.name, currentVal);
          return;
        }
      }
    }

    onChange(field.name, newValue);
  }, [field.name, value, onChange, isMultiline, limitToBox, fitToWidth, fitTextToTextarea]);

  // Intercept paste events — only paste what fits, don't truncate existing text
  const handlePaste = useCallback((e) => {
    const pastedText = e.clipboardData?.getData('text') || '';
    if (!pastedText) return;

    e.preventDefault();

    const el = e.target;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const currentVal = value || '';

    // Build the full candidate value with paste inserted
    const before = currentVal.substring(0, start);
    const after = currentVal.substring(end);

    if (isMultiline && textareaRef.current) {
      const textarea = textareaRef.current;
      // Try pasting progressively less text until it fits
      let bestPaste = pastedText;
      if (maxLength) {
        const maxPasteLen = maxLength - before.length - after.length;
        if (maxPasteLen <= 0) return; // No room
        bestPaste = pastedText.substring(0, maxPasteLen);
      }

      // Binary search: find the longest paste prefix that fits
      let lo = 0, hi = bestPaste.length;
      const prevVal = textarea.value;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        textarea.value = before + bestPaste.substring(0, mid) + after;
        if (textarea.scrollHeight <= textarea.clientHeight + 1) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }
      textarea.value = prevVal;

      if (lo > 0) {
        const result = before + bestPaste.substring(0, lo) + after;
        onChange(field.name, result);
      }
      // If lo === 0, nothing fits — don't paste anything
    } else if (!isMultiline && limitToBox && inputRef.current) {
      const inputEl = inputRef.current;
      const availableWidth = inputEl.clientWidth - 10;
      if (availableWidth <= 0) return;

      // Binary search: find the longest paste prefix that fits width
      let bestPaste = pastedText;
      if (maxLength) {
        const maxPasteLen = maxLength - before.length - after.length;
        if (maxPasteLen <= 0) return;
        bestPaste = pastedText.substring(0, maxPasteLen);
      }

      let lo = 0, hi = bestPaste.length;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        const candidate = before + bestPaste.substring(0, mid) + after;
        if (measureTextWidth(candidate, inputEl) <= availableWidth) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }

      if (lo > 0) {
        const result = before + bestPaste.substring(0, lo) + after;
        onChange(field.name, result);
      }
      // If lo === 0, nothing fits — don't paste anything
    } else {
      // No constraints — paste everything
      const capped = maxLength
        ? (before + pastedText + after).substring(0, maxLength)
        : before + pastedText + after;
      onChange(field.name, capped);
    }
  }, [field.name, value, onChange, isMultiline, limitToBox, maxLength, fitToWidth, fitTextToTextarea]);

  const commonStyle = {
    position: 'absolute',
    ...style,
    border: style.border || '1px solid #ccc',
    fontSize: style.fontSize || '12px',
    fontFamily: style.fontFamily || "'Times New Roman', Times, serif",
    fontWeight: style.fontWeight || 'normal',
    fontStyle: style.fontStyle || 'normal',
    textDecoration: style.textDecoration || 'none',
    color: style.color || '#000000',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    resize: 'none',
    boxSizing: 'border-box',
    padding: '2px 4px'
  };

  if (isMultiline) {
    return (
      <textarea
        ref={(el) => {
          textareaRef.current = el;
          if (typeof ref === 'function') ref(el);
          else if (ref) ref.current = el;
        }}
        className={classNames('pdf-text-field', 'pdf-textarea-field', { 'field-required': required })}
        value={value || ''}
        onChange={handleChange}
        onPaste={handlePaste}
        placeholder={field.label || ''}
        maxLength={maxLength}
        style={{
          ...commonStyle,
          verticalAlign: 'top',
          overflowY: 'hidden',
          overflowX: 'hidden',
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: String(PDF_MULTILINE_LEADING)
        }}
      />
    );
  }

  // Single-line: the box height is pinned to the PDF rect, so the glyphs must be
  // made to fit it. With border-box sizing the usable content height is the
  // declared height minus the border, and any vertical padding would eat into it
  // further — hence 0 vertical padding here. The font is then capped so the full
  // glyph box (ascender + descender) fits, otherwise `overflow: hidden` clips the
  // descenders of letters like g/q/y. lineHeight is pinned to the content height
  // so the text stays vertically centred instead of sitting on the top edge.
  //
  // The border is 1px normally but 2px when the field is flagged with an error
  // (see PdfFieldOverlay), so measure it rather than assuming, or a short field
  // would start clipping again exactly when the user is asked to correct it.
  const borderPx = parseFloat(commonStyle.border) || 1;
  const contentHeight = Math.max(0, (parseFloat(style.height) || 0) - borderPx * 2);
  const requestedFontSize = parseFloat(style.fontSize) || 12;
  const fittedFontSize = contentHeight > 0
    ? Math.max(MIN_FONT_SIZE, Math.min(requestedFontSize, contentHeight / GLYPH_BOX_RATIO))
    : requestedFontSize;

  return (
    <input
      ref={(el) => {
        inputRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      type="text"
      className={classNames('pdf-text-field', { 'field-required': required })}
      value={value || ''}
      onChange={handleChange}
      onPaste={handlePaste}
      placeholder={field.label || ''}
      maxLength={maxLength}
      style={{
        ...commonStyle,
        padding: '0 4px',
        fontSize: `${fittedFontSize}px`,
        lineHeight: contentHeight > 0 ? `${contentHeight}px` : 'normal',
        overflow: 'hidden'
      }}
    />
  );
});

TextField.displayName = 'TextField';

export default TextField;
